import { useMemo } from 'react';

import { GraphEntityType } from '@/components/nodeGraph/utils/types';
import { useGraphQuery } from '@/hooks/useGraphQuery';
import { Risk, TreeNode } from '@/types';
import { mergeStatus } from '@/utils/api';

import {
  createADObjectsQuery,
  createAssetTagsQuery,
  createAssetToRiskQuery,
  createAssetToSeedQuery,
  createAssetToTechnologyQuery,
  createRiskTagsQuery,
  createRiskToAssetQuery,
  createWebpageToAttributeQuery,
} from './queries';

/**
 * Custom hook for fetching all relationship data for the graph.
 * Conditionally fetches different relationships based on clustering entity type.
 *
 * @param assetKeys - Array of asset keys to fetch relationships for
 * @param clusterByEntity - Entity type to cluster the graph by (affects which queries run)
 * @returns Object containing all relationship data and loading states
 */
export const useRelationships = (
  assetKeys: string[],
  clusterByEntity?: GraphEntityType
) => {
  // ASSET -> SEED relationships
  const { data: assetToSeedData, status: assetToSeedStatus } = useGraphQuery({
    tree: true,
    query: createAssetToSeedQuery(),
  });

  // ASSET -> RISK relationships
  const assetToRiskQuery = useMemo(
    () => createAssetToRiskQuery(assetKeys),
    [assetKeys]
  );

  const { data: assetToRiskData, status: assetToRiskStatus } = useGraphQuery({
    tree: true,
    query: assetToRiskQuery,
    enabled: assetKeys.length > 0,
  });

  // Extract risks from query results
  const risks = useMemo(() => {
    return (assetToRiskData || [])
      .map(resp => resp.node.result?.risks?.[0])
      .filter(Boolean) as Risk[];
  }, [assetToRiskData]);

  // ASSET -> TECHNOLOGY relationships
  const technologiesQuery = useMemo(
    () => createAssetToTechnologyQuery(assetKeys),
    [assetKeys]
  );

  const { data: technologiesData, status: technologiesStatus } = useGraphQuery({
    tree: true,
    query: technologiesQuery,
    enabled: assetKeys.length > 0,
  });

  // WEBPAGE -> ATTRIBUTE relationships
  const { data: webpagesData, status: webpagesStatus } = useGraphQuery({
    tree: true,
    query: createWebpageToAttributeQuery(),
  });

  // RISK -> ASSET relationships (reverse lookup)
  const { data: riskToAssetData, status: riskToAssetStatus } = useGraphQuery({
    tree: true,
    enabled: risks.length > 0,
    query: createRiskToAssetQuery(risks),
  });

  // RISK -> VULNERABILITY tags
  const { data: risksTagsData, status: risksTagsStatus } = useGraphQuery({
    tree: true,
    query: createRiskTagsQuery(),
  });

  // ASSET -> ASSET tags
  const { data: assetTagsData, status: assetTagsStatus } = useGraphQuery({
    tree: true,
    query: createAssetTagsQuery(),
  });

  // SHARPHOUND: AD object relationships (only when clustering by SharpHound)
  const { data: adObjectsData, status: adObjectsStatus } = useGraphQuery({
    tree: true,
    enabled: clusterByEntity === 'SharpHound',
    query: createADObjectsQuery(),
  });

  // Calculate combined loading state for related data
  const isLoadingRelatedData =
    clusterByEntity === 'SharpHound'
      ? adObjectsStatus === 'pending'
      : mergeStatus(
          technologiesStatus,
          webpagesStatus,
          assetToRiskStatus,
          risksTagsStatus,
          assetTagsStatus,
          assetToSeedStatus
        ) === 'pending' &&
        (risks.length > 0 ? riskToAssetStatus === 'pending' : true);

  return {
    /** Asset to seed relationship data */
    assetToSeedData: assetToSeedData as TreeNode[] | undefined,
    /** Asset to risk relationship data */
    assetToRiskData: assetToRiskData as TreeNode[] | undefined,
    /** Risk to asset relationship data (reverse lookup) */
    riskToAssetData: riskToAssetData as TreeNode[] | undefined,
    /** Asset to technology relationship data */
    technologiesData: technologiesData as TreeNode[] | undefined,
    /** Webpage to attribute relationship data */
    webpagesData: webpagesData as TreeNode[] | undefined,
    /** Risk to vulnerability tag data */
    risksTagsData: risksTagsData as TreeNode[] | undefined,
    /** Asset tag data */
    assetTagsData: assetTagsData as TreeNode[] | undefined,
    /** AD object relationship data (SharpHound only) */
    adObjectsData: adObjectsData as TreeNode[] | undefined,
    /** Extracted risks from relationship queries */
    risks,
    /** Loading state for all relationship data */
    isLoadingRelatedData,
  };
};
